// Item-selection modal for Convert (2026-05-26). Operator-locked:
// "you should also receive a popup to indicate which products are
// converting (for example, the presentation may have 30 items but the
// client chose 5) - and you should be able to add the quantity of each
// product to convert (again, dupe commonsku)."
//
// Shared between Convert-to-Estimate and Convert-to-Sales-Order on
// the PresentationDetailPage. The modal owns the item-list state +
// quantity-per-item; the parent owns the actual mutation (different
// API call for Estimate vs SO).

import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { seedConvertIncluded } from "./convertSeeding";

export interface ConvertSourceItem {
  id: string;
  name: string;
  /** First product image, or null. Shown as a 40px thumb on the row. */
  imageUrl: string | null;
  /** Smallest client-visible tier quantity on the presentation item — the
   *  default per-line quantity in the modal. Falls back to 1 when the
   *  item has no tiers. */
  defaultQty: number;
  /** 🔴 What distinguishes two copies of the SAME product (2026-08-11) —
   *  "Front · Full colour logo". The same product may appear twice on a
   *  presentation now, and at THIS screen a rep decides which copies convert and
   *  at what quantity. Without it, two rows are literally identical: same name,
   *  same image. Null when the item carries no decoration yet. */
  decoLabel?: string | null;
  /** True when this item has no priced tier. Such an item is SKIPPED by the
   *  converter (orders-builder: `if (!chosenTier) continue`), so the row must
   *  say so here rather than let the rep discover a missing line afterwards. */
  unpriced?: boolean;
}

/** A non-fatal outcome to show after a SUCCESSFUL convert that did less than
 *  the rep asked — e.g. items skipped for having no pricing. Distinct from
 *  `errorMessage`, which means the convert failed. */
export interface ConvertNoticeAction {
  label: string;
  onClick: () => void;
}

export interface ConvertSelectionResult {
  selectedItemIds: string[];
  itemQuantities: Record<string, number>;
}

interface Props {
  /** Title shown in the modal header. e.g. "Convert P-11002 to Estimate". */
  title: string;
  /** CTA label on the primary button. e.g. "Convert to Estimate". */
  ctaLabel: string;
  /** All source items the rep can pick from. */
  sourceItems: ConvertSourceItem[];
  /**
   * 2026-07-26 (B6) — the client's item picks from the public presentation.
   * When present + non-empty, the picker seeds included=true ONLY for these
   * ids (others off) and flags each with a "Client's pick" chip. When null
   * or empty, every row starts checked (unchanged all-on default). The rep
   * can still override any checkbox either way.
   */
  preselectedItemIds?: string[] | null;
  /** True while the parent's mutation is in flight. Disables the form. */
  submitting: boolean;
  /** Error from the parent's mutation, surfaced inside the modal. */
  errorMessage: string | null;
  /** Non-fatal outcome of a SUCCESSFUL convert (e.g. "2 items didn't convert").
   *  Rendered in peach, not rust — nothing failed, it just did less. */
  notice?: string | null;
  noticeAction?: ConvertNoticeAction | null;
  onCancel: () => void;
  onSubmit: (result: ConvertSelectionResult) => void;
}

interface RowState {
  id: string;
  included: boolean;
  quantity: string;
}

export function ConvertItemSelectionModal(props: Props) {
  const {
    title,
    ctaLabel,
    sourceItems,
    preselectedItemIds,
    submitting,
    errorMessage,
  notice,
  noticeAction,
    onCancel,
    onSubmit,
  } = props;

  // 2026-07-26 (B6) — set of the client's picks, used to flag rows with a
  // "Client's pick" chip. Empty when the client made no selections.
  const pickSet = new Set(preselectedItemIds ?? []);
  const hasClientPicks = pickSet.size > 0;

  const [rows, setRows] = useState<RowState[]>(() => {
    // Seed included via the pure decision: client picks → only those checked;
    // no picks → all checked (unchanged default).
    const seeded = seedConvertIncluded(
      sourceItems.map((it) => it.id),
      preselectedItemIds,
    );
    return sourceItems.map((it) => ({
      id: it.id,
      included: seeded[it.id] ?? true,
      quantity: String(it.defaultQty),
    }));
  });
  const [localError, setLocalError] = useState<string | null>(null);

  const setIncluded = (id: string, included: boolean): void => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, included } : r)));
  };
  const setQuantity = (id: string, quantity: string): void => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, quantity } : r)));
  };

  const allOn = rows.every((r) => r.included);
  const toggleAll = (): void => {
    const next = !allOn;
    setRows((prev) => prev.map((r) => ({ ...r, included: next })));
  };

  const submit = (): void => {
    setLocalError(null);
    const selected = rows.filter((r) => r.included);
    if (selected.length === 0) {
      setLocalError("Pick at least one product to convert.");
      return;
    }
    const itemQuantities: Record<string, number> = {};
    for (const r of selected) {
      const n = Number.parseInt(r.quantity, 10);
      if (!Number.isInteger(n) || n <= 0) {
        setLocalError(
          `Quantity for selected products must be a positive whole number.`,
        );
        return;
      }
      itemQuantities[r.id] = n;
    }
    onSubmit({
      selectedItemIds: selected.map((r) => r.id),
      itemQuantities,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-brown-50 px-6 py-4">
          <div>
            <h2 className="font-display text-xl font-semibold text-brown-500">
              {title}
            </h2>
            <p className="mt-1 text-xs text-brown-400">
              Pick which products to carry over and set the quantity for each.
              Pricing comes from the tier bracket that matches the quantity.
            </p>
            {hasClientPicks && (
              <p className="mt-1.5 text-xs font-semibold text-rust-500">
                Pre-checked with the client's picks — adjust anything you like.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-md p-1 text-brown-300 hover:bg-cream-50 hover:text-brown-500 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={toggleAll}
              disabled={submitting}
              className="text-xs font-semibold text-ink underline underline-offset-4 hover:text-muted disabled:opacity-50"
            >
              {allOn ? "Deselect all" : "Select all"}
            </button>
            <p className="text-xs text-brown-300">
              {rows.filter((r) => r.included).length} of {rows.length} selected
            </p>
          </div>

          <ul className="flex flex-col gap-2">
            {sourceItems.map((it) => {
              const row =
                rows.find((r) => r.id === it.id) ??
                ({ id: it.id, included: true, quantity: String(it.defaultQty) } as RowState);
              return (
                <li
                  key={it.id}
                  className="flex items-center gap-3 rounded-md border border-brown-50 bg-cream-50/40 px-3 py-2"
                >
                  <input
                    type="checkbox"
                    checked={row.included}
                    onChange={(e) => setIncluded(it.id, e.target.checked)}
                    disabled={submitting}
                    className="h-4 w-4 rounded border-brown-200 text-rust-400 focus:ring-rust-300"
                    aria-label={`Include ${it.name}`}
                  />
                  <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-md bg-cream-100">
                    {it.imageUrl ? (
                      <img
                        src={it.imageUrl}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-brown-500">
                      {it.name}
                    </p>
                    {/* The decoration is what tells two copies of the same
                        product apart — without it, duplicate rows here are
                        literally identical and the rep is choosing blind. */}
                    {it.decoLabel && (
                      <p className="truncate text-[11px] font-semibold text-rust-500">
                        {it.decoLabel}
                      </p>
                    )}
                    {/* An unpriced item is SKIPPED by the converter. Saying so
                        here, at the moment of choosing, beats letting the rep
                        find a missing line on the estimate afterwards. */}
                    {it.unpriced && (
                      <p className="truncate text-[11px] font-semibold text-brown-400">
                        No pricing — won't convert
                      </p>
                    )}
                    {pickSet.has(it.id) && (
                      <span className="mt-0.5 inline-flex items-center rounded-full bg-peach-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brown-500">
                        Client's pick
                      </span>
                    )}
                  </div>
                  <label className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
                      Qty
                    </span>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      step="1"
                      value={row.quantity}
                      onChange={(e) => setQuantity(it.id, e.target.value)}
                      disabled={submitting || !row.included}
                      className="h-9 w-24 px-2 text-sm"
                      aria-label={`Quantity for ${it.name}`}
                    />
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        {(errorMessage || localError) && (
          <p className="px-6 pb-2 text-sm text-rust-600">
            {localError ?? errorMessage}
          </p>
        )}

        {/* A SUCCESSFUL convert that did less than asked. Peach, not rust —
            nothing failed. The rep stays here until she has read it, then opens
            the document deliberately; the old behaviour navigated away instantly
            and said nothing at all. */}
        {notice && (
          <div className="mx-6 mb-3 rounded-md border border-peach-300 bg-peach-50 px-3 py-2">
            <p className="text-sm text-brown-500">{notice}</p>
            {noticeAction && (
              <button
                type="button"
                onClick={noticeAction.onClick}
                className="mt-1.5 text-sm font-semibold text-ink underline underline-offset-2 hover:text-muted"
              >
                {noticeAction.label}
              </button>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-brown-50 px-6 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={submit}
            disabled={submitting}
          >
            {submitting ? "Converting…" : ctaLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
